import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, orderBy, limit, where, doc, getDoc, onSnapshot, getCountFromServer, updateDoc } from "firebase/firestore";
// Note: getDocs and collection kept for the recent events query
import { formatTimestamp, startOfDayInTimezone } from "../../utils/dateFormat";
import { useLanguage } from "../../context/LanguageContext";
import {
  Users, ClipboardList, Map as MapIcon, Truck, ClipboardCheck,
  AlertTriangle, Target, ArrowRight, ArrowLeft,
  CheckCircle, XCircle, MapPin, Inbox,
  CreditCard, Calendar, Package, CalendarDays, TrendingDown, Clock,
} from "lucide-react";

function Overview({ companyId }) {
  const { t } = useLanguage();
  const [stats, setStats]           = useState(null);
  const [recent, setRecent]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [companyTimezone, setCompanyTimezone] = useState("Asia/Dubai");
  const [visitsToday, setVisitsToday] = useState(0);
  const [billing, setBilling]       = useState(null);

  useEffect(() => {
    if (!companyId) return;

    // Real-time listener — updates automatically when counters change
    const unsubscribe = onSnapshot(
      doc(db, "companies", companyId),
      (snap) => {
        if (!snap.exists()) return;
        const company = snap.data();
        setStats(prev => ({
          activeToday:    prev?.activeToday || 0,
          totalUsers:     company.vendor_count  || 0,
          totalClients:   company.client_count  || 0,
          totalSections:  company.section_count || 0,
          alertsToday:    company.alerts_today  || 0,
          clientsVisited: company.visits_week   || 0,
          weekVisits:     company.visits_week   || 0,
        }));
        setBilling({
          plan:            company.billing_plan            || null,
          balance:         company.billing_balance         ?? null,
          monthly_fee:     company.billing_monthly_fee     || 0,
          last_deduction:  company.billing_last_deduction  || null,
          expiry:          company.subscription_expiry     || null,
          status:          company.subscription_status     || null,
          active:          company.active,
        });
        setLoading(false);
      },
      (err) => {
        console.error("Overview listener error:", err.message);
        setLoading(false);
      }
    );

    // Load recent events separately — still use getDoc for this
    loadRecentEvents();
    loadTodayUniqueVisits();

    // Sync exact client count on mount to fix any drift — own try/catch, never blocks page load
    syncClientCount();

    // Cleanup listener when component unmounts
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function loadTodayUniqueVisits() {
    try {
      const configSnap = await getDoc(doc(db, "companies", companyId, "config", "settings"));
      const tz = configSnap.exists() ? (configSnap.data().timezone || "Asia/Dubai") : "Asia/Dubai";
      const startOfToday = startOfDayInTimezone(tz);
      const snap = await getDocs(query(
        collection(db, "companies", companyId, "events"),
        where("type", "==", "checkin"),
        where("timestamp", ">=", startOfToday)
      ));
      const uniqueClients = new Set(
        snap.docs.map(d => d.data().client_id).filter(Boolean)
      );
      setVisitsToday(uniqueClients.size);
    } catch (err) {
      console.error("loadTodayUniqueVisits:", err.message);
    }
  }

  async function syncClientCount() {
    try {
      const snap = await getCountFromServer(
        collection(db, "companies", companyId, "clients")
      );
      const exactCount = snap.data().count;
      await updateDoc(doc(db, "companies", companyId), {
        client_count: exactCount,
      });
      console.log("[SYNC] client_count synced to:", exactCount);
    } catch (err) {
      console.error("[SYNC] failed:", err.message);
    }
  }

  async function loadRecentEvents() {
    try {
      const [configSnap, eventsSnap] = await Promise.all([
        getDoc(doc(db, "companies", companyId, "config", "settings")),
        getDocs(query(
          collection(db, "companies", companyId, "events"),
          orderBy("timestamp", "desc"),
          limit(10)
        )),
      ]);

      const timezone = configSnap.exists() ? (configSnap.data().timezone || "Asia/Dubai") : "Asia/Dubai";
      setCompanyTimezone(timezone);

      const recentEvents = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Compute activeToday from recent events (approximate from last 10 events)
      const today = startOfDayInTimezone(timezone);
      const todayEvents = recentEvents.filter(e => {
        const ts = e.timestamp?.toDate ? e.timestamp.toDate() : new Date(e.timestamp);
        return ts >= today;
      });
      const activeToday = new Set(todayEvents.map(e => e.vendor_id || e.vendorId).filter(Boolean)).size;

      setRecent(recentEvents);
      setStats(prev => prev ? { ...prev, activeToday } : null);
    } catch (err) {
      console.error("Overview error:", err.message);
    }
  }

  function formatEventDate(ts) {
    return formatTimestamp(ts, companyTimezone);
  }

  const eventColors = {
    visit:         { bg: "bg-green-100",  text: "text-green-700",  Icon: ClipboardCheck },
    checkin:       { bg: "bg-green-100",  text: "text-green-700",  Icon: ClipboardCheck },
    zone_alert:    { bg: "bg-red-100",    text: "text-red-700",    Icon: AlertTriangle  },
    session_start: { bg: "bg-blue-100",   text: "text-blue-700",   Icon: CheckCircle    },
    session_end:   { bg: "bg-gray-100",   text: "text-gray-600",   Icon: XCircle        },
    zone_entry:              { bg: "bg-purple-100", text: "text-purple-700", Icon: ArrowRight    },
    zone_exit:               { bg: "bg-orange-100", text: "text-orange-700", Icon: ArrowLeft     },
    zone_entry_unauthorized: { bg: "bg-red-100",    text: "text-red-700",    Icon: AlertTriangle },
  };

  if (loading || !stats) return (
    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
      <p className="text-gray-400 text-sm">{t("overview_loading")}</p>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800">{t("overview_title")}</h2>
        <p className="text-gray-500 text-sm mt-1">
          {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { Icon: Users,         label: t("overview_total_users"),   value: stats.totalUsers,    color: "blue",   sub: t("overview_registered_vendors") },
          { Icon: ClipboardList, label: t("overview_total_clients"), value: stats.totalClients,  color: "green",  sub: t("overview_in_database") },
          { Icon: MapIcon,       label: t("overview_zones"),         value: stats.totalSections, color: "purple", sub: t("overview_geofences") },
          { Icon: Truck,         label: t("overview_active_today"),  value: stats.activeToday,   color: "orange", sub: t("overview_vendors_on_field") },
        ].map((card, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm p-5">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
              card.color === "blue"   ? "bg-blue-50 text-blue-600" :
              card.color === "green"  ? "bg-green-50 text-green-600" :
              card.color === "purple" ? "bg-purple-50 text-purple-600" : "bg-orange-50 text-orange-600"
            }`}><card.Icon className="w-5 h-5" /></div>
            <p className={`text-2xl font-bold ${
              card.color === "blue"   ? "text-blue-700" :
              card.color === "green"  ? "text-green-700" :
              card.color === "purple" ? "text-purple-700" : "text-orange-700"
            }`}>{card.value}</p>
            <p className="text-gray-700 text-sm font-medium mt-1">{card.label}</p>
            <p className="text-gray-400 text-xs mt-0.5">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Today's activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { Icon: ClipboardCheck, label: t("overview_clients_visited_today"), value: visitsToday,          color: "text-green-600", bg: "bg-green-50"  },
          { Icon: AlertTriangle,  label: t("overview_alerts_today"),          value: stats.alertsToday,    color: "text-red-600",   bg: "bg-red-50"    },
          { Icon: Target,         label: t("overview_visits_this_week"),      value: stats.clientsVisited, color: "text-blue-600",  bg: "bg-blue-50"   },
        ].map((card, i) => (
          <div key={i} className={`rounded-xl p-5 ${card.bg}`}>
            <card.Icon className={`w-7 h-7 mb-2 ${card.color}`} />
            <p className={`text-3xl font-bold ${card.color}`}>{card.value}</p>
            <p className="text-gray-600 text-sm mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Subscription Info (read-only) */}
      {billing && billing.plan && (
        <SubscriptionPanel billing={billing} />
      )}

      {/* Recent activity feed */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-semibold text-gray-700">{t("overview_recent_activity")}</h3>
          <span className="text-xs text-gray-400">{t("overview_last_10_events")}</span>
        </div>

        {recent.length === 0 ? (
          <div className="text-center py-8">
            <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">{t("overview_no_activity")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recent.map((event, i) => {
              const meta = eventColors[event.type] || { bg: "bg-gray-100", text: "text-gray-600", Icon: MapPin };
              const EventIcon = meta.Icon;
              return (
                <div key={event.id || i} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${meta.bg} ${meta.text}`}>
                    <EventIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate">
                      <span className="font-medium">{event.vendor_name || event.vendorName || t("overview_unknown_vendor")}</span>
                      {event.type === "visit"         && ` ${t("overview_visited")} ${event.client_name || event.clientName || "a client"}`}
                      {event.type === "checkin"       && ` ${t("overview_checked_in_at")} ${event.client_name || event.clientName || "a client"}`}
                      {event.type === "zone_alert"    && ` ${t("overview_triggered_alert")} ${event.sectionName || "a zone"}`}
                      {event.type === "session_start" && ` ${t("overview_started_session")}`}
                      {event.type === "session_end"   && ` ${t("overview_ended_session")}`}
                      {event.type === "zone_entry"              && ` ${t("overview_entered_zone")} ${event.sectionName || "a zone"}`}
                      {event.type === "zone_exit"               && ` ${t("overview_exited_zone")} ${event.sectionName || "a zone"}`}
                      {event.type === "zone_entry_unauthorized" && ` ${t("overview_unauth_zone")}: ${event.sectionName || "unknown"}`}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">{formatEventDate(event.timestamp)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${meta.bg} ${meta.text}`}>
                    {event.type}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}

// ── Subscription Info Panel (read-only for admin) ─────────────────────────
function SubscriptionPanel({ billing }) {
  const { t } = useLanguage();
  const { plan, balance, monthly_fee, last_deduction, expiry, status, active } = billing;

  const dailyRate = monthly_fee > 0 ? parseFloat((monthly_fee / 30).toFixed(2)) : 0;
  const daysLeft  = (plan === "monthly" && dailyRate > 0 && balance != null)
    ? Math.floor(balance / dailyRate)
    : null;

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const isExpired = plan === "expiry_date" && expiry
    ? (expiry.toDate ? expiry.toDate() : new Date(expiry)) < new Date()
    : false;

  const statusColor = () => {
    if (active === false)    return { bg: "bg-red-50",   text: "text-red-700",   dot: "bg-red-500",   label: t("lbl_account_suspended") || "Suspended" };
    if (status === "unpaid") return { bg: "bg-red-50",   text: "text-red-700",   dot: "bg-red-500",   label: t("lbl_unpaid")            || "Unpaid"    };
    if (status === "paid")   return { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500", label: t("active")                || "Active"    };
    if (status === "trial")  return { bg: "bg-blue-50",  text: "text-blue-700",  dot: "bg-blue-500",  label: t("lbl_trial")             || "Trial"     };
    return                          { bg: "bg-gray-50",  text: "text-gray-700",  dot: "bg-gray-400",  label: status || "Unknown" };
  };
  const st = statusColor();

  const planLabel = plan === "monthly"     ? (t("lbl_monthly_billing")  || "Monthly Billing")
                  : plan === "forever"     ? (t("lbl_active_forever")   || "Active Forever")
                  : plan === "expiry_date" ? (t("lbl_expiry_by_date")   || "Expiry by Date")
                  : plan;

  const PlanIcon = plan === "monthly"     ? TrendingDown
                 : plan === "forever"     ? Package
                 : plan === "expiry_date" ? CalendarDays
                 : CreditCard;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <CreditCard className="w-4 h-4" /> {t("lbl_subscription_billing") || "Subscription & Billing"}
        </h3>
        <span className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${st.bg} ${st.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
          {st.label}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

        {/* Plan type */}
        <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-gray-400 mb-1">
            <PlanIcon className="w-3.5 h-3.5" />
            <span className="text-xs">{t("lbl_plan_type_label") || "Plan"}</span>
          </div>
          <p className="text-sm font-semibold text-gray-800">{planLabel}</p>
        </div>

        {/* Monthly plan cards */}
        {plan === "monthly" && (
          <>
            <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                <Calendar className="w-3.5 h-3.5" />
                <span className="text-xs">{t("lbl_monthly_fee_label") || "Monthly Fee"}</span>
              </div>
              <p className="text-sm font-semibold text-gray-800">{monthly_fee} AED</p>
              <p className="text-xs text-gray-400">{dailyRate} AED {t("lbl_per_day") || "/ day"}</p>
            </div>

            <div className={`rounded-xl p-4 flex flex-col gap-1 ${balance != null && balance <= 0 ? "bg-red-50" : "bg-gray-50"}`}>
              <div className={`flex items-center gap-1.5 mb-1 ${balance != null && balance <= 0 ? "text-red-400" : "text-gray-400"}`}>
                <TrendingDown className="w-3.5 h-3.5" />
                <span className="text-xs">{t("lbl_current_balance_display") || "Current Balance"}</span>
              </div>
              <p className={`text-sm font-bold ${balance != null && balance <= 0 ? "text-red-600" : "text-gray-800"}`}>
                {balance != null ? `${parseFloat(balance).toFixed(2)} AED` : "—"}
              </p>
              {daysLeft !== null && (
                <p className={`text-xs ${daysLeft <= 7 ? "text-red-500 font-medium" : "text-gray-400"}`}>
                  ≈ {daysLeft} {t("lbl_days_remaining_approx") || "days remaining"}
                </p>
              )}
            </div>

            <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                <Clock className="w-3.5 h-3.5" />
                <span className="text-xs">{t("lbl_last_deduction") || "Last Deduction"}</span>
              </div>
              <p className="text-sm font-semibold text-gray-800">
                {last_deduction ? formatDate(last_deduction) : (t("lbl_not_yet") || "Not yet")}
              </p>
              {last_deduction && (
                <p className="text-xs text-gray-400">
                  {(() => {
                    const d    = last_deduction.toDate ? last_deduction.toDate() : new Date(last_deduction);
                    const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));
                    if (diff < 24) return (t("lbl_h_ago") || "{n}h ago").replace("{n}", diff);
                    return (t("lbl_d_ago") || "{n}d ago").replace("{n}", Math.floor(diff / 24));
                  })()}
                </p>
              )}
            </div>
          </>
        )}

        {/* Forever plan */}
        {plan === "forever" && (
          <div className="bg-green-50 rounded-xl p-4 col-span-3 flex items-center gap-3">
            <Package className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700 font-medium">
              {t("lbl_active_forever_billing_msg") || "This account is active forever — no balance or expiry tracking."}
            </p>
          </div>
        )}

        {/* Expiry date plan */}
        {plan === "expiry_date" && (
          <div className={`rounded-xl p-4 col-span-3 flex items-center gap-3 ${isExpired ? "bg-red-50" : "bg-purple-50"}`}>
            <CalendarDays className={`w-5 h-5 flex-shrink-0 ${isExpired ? "text-red-600" : "text-purple-600"}`} />
            <div>
              <p className={`text-sm font-semibold ${isExpired ? "text-red-700" : "text-purple-800"}`}>
                {isExpired
                  ? (t("lbl_subscription_expired_title") || "Subscription Expired")
                  : (t("lbl_active_until") || "Active until")}{" "}
                {formatDate(expiry)}
              </p>
              {!isExpired && expiry && (
                <p className="text-xs text-purple-600 mt-0.5">
                  {(() => {
                    const d            = expiry.toDate ? expiry.toDate() : new Date(expiry);
                    const daysRemaining = Math.ceil((d - Date.now()) / (1000 * 60 * 60 * 24));
                    return `${daysRemaining} ${t("lbl_days_remaining_approx") || "days remaining"}`;
                  })()}
                </p>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Low balance warning */}
      {plan === "monthly" && balance != null && balance > 0 && daysLeft !== null && daysLeft <= 7 && (
        <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <p className="text-xs text-orange-700 font-medium">
            {(t("lbl_low_balance_warning") || "Low balance — your subscription will be suspended in approximately {days} day(s). Please contact your administrator to add funds.").replace("{days}", daysLeft)}
          </p>
        </div>
      )}
      {plan === "monthly" && balance != null && balance <= 0 && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-700 font-medium">
            {t("lbl_suspended_balance_warning") || "Your subscription has been suspended due to insufficient balance. Please contact your administrator."}
          </p>
        </div>
      )}
    </div>
  );
}

export default Overview;